import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "@/lib/api";
import { Container, PageTitle, NbCard, Chip, NbButton } from "@/components/nb";
import { Stagger, Item } from "@/components/motion";

export default function ResultHistory() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/study/diagnostic/results").then((r) => setResults(r.data)).finally(() => setLoading(false));
  }, []);

  // Prepare chart data — reverse to chronological
  const chartData = [...results].reverse().map((r, i) => ({
    n: i + 1,
    pct: r.pct,
    label: r.title || r.subject,
    date: new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
  }));

  const bySubject = {};
  results.forEach((r) => {
    const key = r.title || r.subject;
    if (!bySubject[key]) bySubject[key] = [];
    bySubject[key].push(r);
  });

  return (
    <Container>
      <PageTitle eyebrow="RESULT HISTORY" title="Every attempt, in one place." sub="Watch yourself level up over time." />

      {loading && <NbCard>Loading your history...</NbCard>}
      {!loading && results.length === 0 && (
        <NbCard color="tertiary" className="!p-8" data-testid="empty-history">
          <div className="font-heading font-black text-2xl tracking-tight">No attempts yet.</div>
          <p className="text-neutral-800 mt-2">Take your first diagnostic and it'll show up here.</p>
          <div className="mt-4"><Link to="/study/diagnostic"><NbButton color="black">Take a diagnostic →</NbButton></Link></div>
        </NbCard>
      )}

      {!loading && results.length > 0 && (
        <>
          <NbCard className="!p-6 mb-6" data-testid="progress-chart">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-black text-xl tracking-tight">Score over time</h3>
              <Chip color="primary">{results.length} ATTEMPTS</Chip>
            </div>
            <div className="w-full h-64">
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#0A0A0A" strokeOpacity={0.1} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#0A0A0A" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#0A0A0A" />
                  <Tooltip contentStyle={{ border: "2px solid #0A0A0A", borderRadius: 8, background: "#fff" }} />
                  <Line type="monotone" dataKey="pct" stroke="#0A0A0A" strokeWidth={3} dot={{ fill: "#B4F8C8", stroke: "#0A0A0A", strokeWidth: 2, r: 5 }} activeDot={{ r: 7 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </NbCard>

          <h3 className="font-heading font-black text-2xl tracking-tight mb-4">All attempts</h3>
          <Stagger className="space-y-3" data-testid="attempt-list">
            {results.map((r) => (
              <Item key={r.id}>
                <NbCard className="!p-4 flex items-center gap-4 flex-wrap">
                  <div className="w-16 text-center">
                    <div className="font-heading font-black text-2xl">{r.pct}%</div>
                    <div className="font-mono-sl text-[10px] uppercase">{r.level}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold">{r.title || r.subject}</div>
                    <div className="font-mono-sl text-[10px] uppercase text-neutral-600">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  {r.badges?.length > 0 && (
                    <div className="flex gap-1">
                      {r.badges.map((b) => <span key={b.id} title={b.title} className="text-xl">{b.emoji}</span>)}
                    </div>
                  )}
                  {r.avg_time_per_q != null && <Chip color="tertiary">{r.avg_time_per_q}s / Q</Chip>}
                </NbCard>
              </Item>
            ))}
          </Stagger>
        </>
      )}
    </Container>
  );
}
